/**
 * Inline SVG icons.
 *
 * Inline rather than an icon package for two reasons: no runtime dependency to
 * keep current, and `stroke="currentColor"` means every icon follows the theme
 * token of whatever it sits inside with no per-icon colour rules.
 *
 * All icons are 16x16 on a 24-unit viewBox, stroked, never filled.
 */

function Icon({ children, size = 16, ...rest }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const IconCatalogue = (props) => (
  <Icon {...props}>
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" />
  </Icon>
);

export const IconRegistry = (props) => (
  <Icon {...props}>
    <path d="M3 21h18" />
    <path d="M5 21V7l7-4 7 4v14" />
    <path d="M9 9h1M9 13h1M9 17h1M14 9h1M14 13h1M14 17h1" />
  </Icon>
);

export const IconLog = (props) => (
  <Icon {...props}>
    <path d="M8 2v4M16 2v4M3 10h18" />
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M8 14h.01M12 14h4M8 18h.01M12 18h4" />
  </Icon>
);

export const IconSettings = (props) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
  </Icon>
);

export const IconSearch = (props) => (
  <Icon {...props}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </Icon>
);

export const IconMoon = (props) => (
  <Icon {...props}>
    <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
  </Icon>
);

// Half-filled circle: the neutral black theme.
export const IconContrast = (props) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 3a9 9 0 0 1 0 18Z" fill="currentColor" />
  </Icon>
);

export const IconSun = (props) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </Icon>
);

export const IconLogout = (props) => (
  <Icon {...props}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="m16 17 5-5-5-5M21 12H9" />
  </Icon>
);

export const IconCheck = (props) => (
  <Icon {...props}>
    <path d="m20 6-11 11-5-5" />
  </Icon>
);

export const IconEdit = (props) => (
  <Icon {...props}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </Icon>
);

export const IconAlert = (props) => (
  <Icon {...props}>
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
    <path d="M12 9v4M12 17h.01" />
  </Icon>
);

export const IconInfo = (props) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 16v-4M12 8h.01" />
  </Icon>
);

export const IconClose = (props) => (
  <Icon {...props}>
    <path d="M18 6 6 18M6 6l12 12" />
  </Icon>
);

export const IconKey = (props) => (
  <Icon {...props}>
    <circle cx="7.5" cy="15.5" r="4.5" />
    <path d="m10.7 12.3 8.3-8.3M17 6l2.5 2.5M14.5 8.5 17 11" />
  </Icon>
);

/* Shown while a secret is hidden - clicking reveals it. */
export const IconEye = (props) => (
  <Icon {...props}>
    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </Icon>
);

export const IconEyeOff = (props) => (
  <Icon {...props}>
    <path d="M10.6 5.2A9.7 9.7 0 0 1 12 5c6.4 0 10 7 10 7a17.7 17.7 0 0 1-2.7 3.7M6.3 6.8A17.4 17.4 0 0 0 2 12s3.6 7 10 7a9.6 9.6 0 0 0 4.2-.9" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2M3 3l18 18" />
  </Icon>
);

/* Test connection: a plug reaching for a socket. */
export const IconPlug = (props) => (
  <Icon {...props}>
    <path d="M9 2v6M15 2v6" />
    <path d="M6 8h12v3a6 6 0 0 1-12 0V8Z" />
    <path d="M12 17v5" />
  </Icon>
);

export const IconTrash = (props) => (
  <Icon {...props}>
    <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
    <path d="M19 6v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6M10 11v6M14 11v6" />
  </Icon>
);

export const IconConstruction = (props) => (
  <Icon {...props}>
    <rect x="2" y="6" width="20" height="8" rx="1" />
    <path d="M17 14v7M7 14v7M17 3v3M7 3v3M10 14 2.3 6.3M14 6l7.7 7.7M8 6l8 8" />
  </Icon>
);

/* --- Interaction icons ---------------------------------------------------- */

export const IconPlus = (props) => (
  <Icon {...props}>
    <path d="M12 5v14M5 12h14" />
  </Icon>
);

export const IconChevronLeft = (props) => (
  <Icon {...props}>
    <path d="m15 18-6-6 6-6" />
  </Icon>
);

export const IconDownload = (props) => (
  <Icon {...props}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="m7 10 5 5 5-5" />
    <path d="M12 15V3" />
  </Icon>
);

export const IconFilter = (props) => (
  <Icon {...props}>
    <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
  </Icon>
);

export const IconChevronRight = (props) => (
  <Icon {...props}>
    <path d="m9 18 6-6-6-6" />
  </Icon>
);

export const IconChevronDown = (props) => (
  <Icon {...props}>
    <path d="m6 9 6 6 6-6" />
  </Icon>
);

export const IconBasket = (props) => (
  <Icon {...props}>
    <path d="M5 11h14l-1.2 8.2a2 2 0 0 1-2 1.8H8.2a2 2 0 0 1-2-1.8L5 11Z" />
    <path d="m9 11 1.5-7M15 11 13.5 4" />
  </Icon>
);

/**
 * Filled when starred (`filled` prop), outlined otherwise - the one icon in
 * this file whose fill is meaningful rather than always "none", because a
 * favorite star needs to read as on/off at a glance, not just on hover.
 */
export const IconStar = ({ filled = false, ...props }) => (
  <Icon {...props} fill={filled ? 'currentColor' : 'none'}>
    <path d="m12 2.5 2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.6l-5.9 3 1.3-6.6-4.9-4.6 6.6-.8L12 2.5Z" />
  </Icon>
);

export const IconClock = (props) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3.5 3.5" />
  </Icon>
);

export const IconListPlus = (props) => (
  <Icon {...props}>
    <path d="M4 6h11M4 12h11M4 18h6" />
    <path d="M18 15v6M15 18h6" />
  </Icon>
);

export const IconBuildingSearch = (props) => (
  <Icon {...props}>
    <path d="M3 21h8V3H3v18ZM6 7h2M6 11h2M6 15h2" />
    <circle cx="16.5" cy="12.5" r="3.5" />
    <path d="m19 15 2.5 2.5" />
  </Icon>
);

export const IconTree = (props) => (
  <Icon {...props}>
    <path d="M4 4h6M4 4v16M4 12h6M4 20h6" />
    <rect x="12" y="2" width="9" height="4" rx="1" />
    <rect x="12" y="10" width="9" height="4" rx="1" />
    <rect x="12" y="18" width="9" height="4" rx="1" />
  </Icon>
);

/* --- Category icons ------------------------------------------------------- */
/* Keyed by the `icon` field in data/sic-categories.json. A category naming an
   icon that is not here falls back to IconDots rather than rendering nothing. */

const IconChip = (props) => (
  <Icon {...props}>
    <rect x="7" y="7" width="10" height="10" rx="1.5" />
    <path d="M10 2v3M14 2v3M10 19v3M14 19v3M2 10h3M2 14h3M19 10h3M19 14h3" />
  </Icon>
);

const IconPeople = (props) => (
  <Icon {...props}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
    <path d="M16 5.5a3.2 3.2 0 0 1 0 6M17.5 14.2A6.5 6.5 0 0 1 21.5 20" />
  </Icon>
);

const IconHammer = (props) => (
  <Icon {...props}>
    <path d="m14.5 5.5 4 4M12 8l4-4 4 4-4 4-4-4Z" />
    <path d="m13 11-8 8a2.1 2.1 0 0 0 3 3l8-8" />
  </Icon>
);

const IconHeart = (props) => (
  <Icon {...props}>
    <path d="M12 20s-7-4.3-7-9.3A4.2 4.2 0 0 1 12 8a4.2 4.2 0 0 1 7 2.7c0 5-7 9.3-7 9.3Z" />
  </Icon>
);

const IconCart = (props) => (
  <Icon {...props}>
    <path d="M2 3h2.5l2.2 11.2a1.8 1.8 0 0 0 1.8 1.4h8.6a1.8 1.8 0 0 0 1.8-1.4L20.5 7H6" />
    <circle cx="9" cy="20" r="1.4" />
    <circle cx="18" cy="20" r="1.4" />
  </Icon>
);

const IconTruck = (props) => (
  <Icon {...props}>
    <path d="M2 6h11v10H2zM13 9h4.5l3.5 3.5V16h-8" />
    <circle cx="6.5" cy="18" r="1.8" />
    <circle cx="17.5" cy="18" r="1.8" />
  </Icon>
);

const IconCoins = (props) => (
  <Icon {...props}>
    <ellipse cx="9" cy="6" rx="6" ry="2.6" />
    <path d="M3 6v5c0 1.4 2.7 2.6 6 2.6s6-1.2 6-2.6V6" />
    <path d="M15 12.4c3 .2 6 1.3 6 2.6v4c0 1.4-2.7 2.6-6 2.6s-6-1.2-6-2.6v-4" />
  </Icon>
);

const IconBuilding = (props) => (
  <Icon {...props}>
    <path d="M3 21h18M5 21V6l7-3 7 3v15" />
    <path d="M9 21v-4h6v4M9 8h1M9 12h1M14 8h1M14 12h1" />
  </Icon>
);

const IconCup = (props) => (
  <Icon {...props}>
    <path d="M4 8h13v6a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5V8Z" />
    <path d="M17 9.5h1.5a2.5 2.5 0 0 1 0 5H17M6 2v3M10 2v3M14 2v3" />
  </Icon>
);

const IconFactory = (props) => (
  <Icon {...props}>
    <path d="M3 21h18M4 21V10l6 4V10l6 4V6h4v15" />
    <path d="M7 17h1M12 17h1M18 17h1" />
  </Icon>
);

const IconBriefcase = (props) => (
  <Icon {...props}>
    <rect x="2.5" y="7" width="19" height="13" rx="2" />
    <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M2.5 12.5h19" />
  </Icon>
);

const IconCap = (props) => (
  <Icon {...props}>
    <path d="m2 8 10-4 10 4-10 4L2 8Z" />
    <path d="M6 10v5c0 1.7 2.7 3 6 3s6-1.3 6-3v-5M22 8v6" />
  </Icon>
);

const IconPalette = (props) => (
  <Icon {...props}>
    <path d="M12 3a9 9 0 0 0 0 18c1.1 0 1.8-.8 1.8-1.7 0-.5-.2-.9-.5-1.2-.3-.3-.5-.7-.5-1.1 0-1 .8-1.7 1.7-1.7H16a5 5 0 0 0 5-5c0-4-4-7.3-9-7.3Z" />
    <circle cx="7.5" cy="11" r="1" />
    <circle cx="10" cy="7" r="1" />
    <circle cx="15" cy="7.5" r="1" />
  </Icon>
);

const IconBolt = (props) => (
  <Icon {...props}>
    <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" />
  </Icon>
);

const IconLeaf = (props) => (
  <Icon {...props}>
    <path d="M4 20c0-9 6-14 16-14 0 10-5 15-13 15-2 0-3-.5-3-1Z" />
    <path d="M9 15c2-3 5-5 8-6" />
  </Icon>
);

const IconCar = (props) => (
  <Icon {...props}>
    <path d="M4 16v3M20 16v3M3 16h18v-4l-2-1-1.5-4a2 2 0 0 0-1.9-1.4H8.4A2 2 0 0 0 6.5 10L5 14l-2 1v1Z" />
    <path d="M6 16h2M16 16h2" />
  </Icon>
);

const IconScales = (props) => (
  <Icon {...props}>
    <path d="M12 3v18M7 21h10M4 8h16M12 4 4 8l-2 5a4 4 0 0 0 8 0L8 8M12 4l8 4 2 5a4 4 0 0 1-8 0l2-5" />
  </Icon>
);

const IconTrophy = (props) => (
  <Icon {...props}>
    <path d="M8 3h8v6a4 4 0 0 1-8 0V3Z" />
    <path d="M8 5H5v2a3 3 0 0 0 3 3M16 5h3v2a3 3 0 0 1-3 3M12 13v4M9 21h6l-1-4h-4l-1 4Z" />
  </Icon>
);

const IconHands = (props) => (
  <Icon {...props}>
    <path d="M12 20s-7-4.3-7-9.3A4.2 4.2 0 0 1 12 8a4.2 4.2 0 0 1 7 2.7c0 5-7 9.3-7 9.3Z" />
    <path d="M12 8V3M9.5 5.5 12 3l2.5 2.5" />
  </Icon>
);

const IconDots = (props) => (
  <Icon {...props}>
    <circle cx="5" cy="12" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="19" cy="12" r="1.5" />
  </Icon>
);

export const CATEGORY_ICONS = {
  chip: IconChip,
  people: IconPeople,
  hammer: IconHammer,
  heart: IconHeart,
  cart: IconCart,
  truck: IconTruck,
  coins: IconCoins,
  building: IconBuilding,
  cup: IconCup,
  factory: IconFactory,
  briefcase: IconBriefcase,
  cap: IconCap,
  palette: IconPalette,
  bolt: IconBolt,
  leaf: IconLeaf,
  car: IconCar,
  scales: IconScales,
  trophy: IconTrophy,
  hands: IconHands,
  dots: IconDots,
};

export const categoryIcon = (name) => CATEGORY_ICONS[name] ?? IconDots;

/* --- Acquisition icons (phase 4) ------------------------------------------ */

export const IconPlay = (props) => (
  <Icon {...props}>
    <path d="M6 4.5v15l13-7.5z" />
  </Icon>
);

export const IconPause = (props) => (
  <Icon {...props}>
    <path d="M9 5v14M15 5v14" />
  </Icon>
);

export const IconStop = (props) => (
  <Icon {...props}>
    <rect x="6" y="6" width="12" height="12" rx="1.5" />
  </Icon>
);

/**
 * The only animated icon. The spin lives in App.css as `.icon-spin` so no
 * component carries its own animation.
 */
export const IconSpinner = (props) => (
  <Icon {...props} className="icon-spin">
    <path d="M12 3a9 9 0 1 0 9 9" />
  </Icon>
);

/* --- Registry icons (phase 6) ---------------------------------------------- */

/**
 * Marks a link that leaves the application - the Companies House record for a
 * company, or a discovered website. Always paired with `target="_blank"`, so the
 * icon is the thing that warns before the click rather than after it.
 */
export const IconExternal = (props) => (
  <Icon {...props}>
    <path d="M14 4h6v6M20 4l-8 8" />
    <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
  </Icon>
);

export const IconRefresh = (props) => (
  <Icon {...props}>
    <path d="M3 12a9 9 0 0 1 15.4-6.4L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-15.4 6.4L3 16" />
    <path d="M3 21v-5h5" />
  </Icon>
);

/* --- Log view switcher (phase 5 follow-up) --------------------------------- */

export const IconLayoutGrid = (props) => (
  <Icon {...props}>
    <rect x="3" y="3" width="8" height="8" rx="1.5" />
    <rect x="13" y="3" width="8" height="8" rx="1.5" />
    <rect x="3" y="13" width="8" height="8" rx="1.5" />
    <rect x="13" y="13" width="8" height="8" rx="1.5" />
  </Icon>
);

export const IconLayoutList = (props) => (
  <Icon {...props}>
    <path d="M8 6h13M8 12h13M8 18h13" />
    <path d="M3 6h.01M3 12h.01M3 18h.01" />
  </Icon>
);

export const IconLayoutSidebar = (props) => (
  <Icon {...props}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M9 3v18" />
  </Icon>
);

/* --- Contact tab (phase 10 redesign) --------------------------------------- */

export const IconCopy = (props) => (
  <Icon {...props}>
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </Icon>
);
